import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  signal,
  inject,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { KeycloakGroupResponse } from '@app/api/user-service/model/keycloakGroupResponse';
import { TreeModule } from 'primeng/tree';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService, TreeNode } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  RoleStoreService,
  SelectedSidebarItem,
} from '../../services/rolestore.service';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  FormFieldConfig,
  SearchFormComponent,
  SearchFormData,
} from '@shared/components/search-form/search-form.component';
import { AddRoleDialogComponent } from '../add-role-dialog/add-role-dialog.component';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';

// Role interface for sidebar
export interface RoleSidebarModel {
  id?: number;
  name: string;
  code: string;
  description?: string;
  statusFlag?: boolean;
  userCount?: number;
  permissions?: string[];
  children?: RoleSidebarModel[];
}

@Component({
  selector: 'app-roles-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    TreeModule,
    TranslateModule,
    TooltipModule,
    AppButtonComponent,
    SearchFormComponent,
  ],
  templateUrl: './roles-sidebar.component.html',
  styleUrls: ['./roles-sidebar.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DialogService],
})
export class RolesSidebarComponent implements OnInit, OnChanges {
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private cdr = inject(ChangeDetectorRef);
  private translateService = inject(TranslateService);
  private roleStoreService = inject(RoleStoreService);
  private readonly dialogService = inject(DialogService);
  private readonly messageService = inject(MessageService);
  sidebarTreeData$ = this.roleStoreService.sidebarTreeData$;
  private dialogRef: DynamicDialogRef | null = null;
  // Tree data
  treeData = signal<TreeNode[]>([]);
  filteredTreeData = signal<TreeNode[]>([]);
  selectedNode = signal<TreeNode | null>(null);
  loading = signal(false);
  searchText = signal('');
  roleName = signal('');
  roleDescription = signal('');
  roleSearchConfig: FormFieldConfig[] = [
    {
      key: 'roleName',
      label: 'configuration.roles.search.role_name',
      type: 'text',
      width: '100%',
    },
    // {
    //   key: 'roleDescription',
    //   label: 'configuration.roles.table.description',
    //   type: 'text',
    //   width: '100%',
    // },
  ];

  // Output events
  @Output() roleSelect = new EventEmitter<RoleSidebarModel | null>();
  @Output() deleteRoleRequest = new EventEmitter<RoleSidebarModel>();

  // Input to clear selection when search form is used
  @Input() canCreate = false;
  @Input() canDelete = false;
  @Input() clearSelection = false;

  constructor() {}

  ngOnInit(): void {
    this.loading.set(true);

    // Load roles from the shared store (API called once)
    this.roleStoreService.loadRoles();

    // Subscribe to loading state from store
    this.roleStoreService.loading$.subscribe({
      next: (isLoading) => {
        this.loading.set(isLoading);
        this.cdr.detectChanges();
      },
    });

    // Subscribe to original data to process tree data
    this.roleStoreService.originalData$.subscribe({
      next: (roles: KeycloakGroupResponse[] | null) => {
        if (roles) {
          // Filter to show only root level items for sidebar
          const rootRoles = roles.filter((role) => !role.parentId);

          // Convert to TreeNode format
          const treeNodes = this.convertToTreeNodes(rootRoles);

          this.treeData.set(treeNodes);
          this.filteredTreeData.set(treeNodes);
          this.cdr.detectChanges();
        }
      },
      error: (error: any) => {
        console.error(
          'RolesSidebarComponent: Error loading original data:',
          error,
        );
        this.treeData.set([]);
        this.filteredTreeData.set([]);
        this.cdr.detectChanges();
      },
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['clearSelection'] &&
      changes['clearSelection'].currentValue === true
    ) {
      this.clearTreeSelection();
    }
  }

  /**
   * Clear tree selection and emit null
   */
  clearTreeSelection(): void {
    this.selectedNode.set(null);
    this.roleSelect.emit(null);
    this.cdr.detectChanges();
  }

  /**
   * Handle search input changes
   */
  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const value = target.value;
    this.searchText.set(value);
    this.filterTreeData(value);
  }

  onRoleFormSearch(formData: SearchFormData): void {
    const roleName = (formData['roleName'] || '').toString().trim();

    this.roleName.set(roleName);
    this.roleDescription.set(
      (formData['roleDescription'] || '').toString().trim(),
    );
    this.resetSelectionForSearch();
    this.roleStoreService.searchRoles({ name: roleName });
  }

  onRoleFormReset(): void {
    this.roleName.set('');
    this.roleDescription.set('');
    this.resetSelectionForSearch();
    this.roleStoreService.searchRoles({ name: '' });
  }

  onCreateRole(): void {
    if (!this.canCreate) {
      return;
    }

    const header = this.translateService.instant(
      'configuration.roles.dialog.create_role',
    );

    this.dialogRef = this.dialogService.open(AddRoleDialogComponent, {
      header,
      width: '600px',
      modal: true,
      closable: false,
      dismissableMask: false,
      closeOnEscape: false,
      focusOnShow: false,
      data: { mode: 'role', messageService: this.messageService },
    });

    this.dialogRef.onClose.subscribe((result) => {
      this.dialogRef = null;
      if (result?.toast) {
        this.messageService.add({
          severity: result.toast.severity,
          summary: this.translateService.instant(result.toast.summaryKey),
          detail: this.translateService.instant(result.toast.detailKey),
        });
      }

      if (!result?.created || !result?.roleName) {
        return;
      }

      this.selectCreatedRole(result.roleName);
    });
  }

  onDeleteRole(event: Event, role: RoleSidebarModel): void {
    event.stopPropagation();

    if (!this.canDelete || !role?.id) {
      return;
    }

    this.deleteRoleRequest.emit(role);
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchText.set('');
    this.filterTreeData('');
  }

  /**
   * Filter tree data based on search text
   */
  private filterTreeData(searchText: string): void {
    if (!searchText.trim()) {
      this.filteredTreeData.set(this.treeData());
      return;
    }

    const normalizedSearch = searchText.toLowerCase();

    const filtered: TreeNode[] = [];
    this.treeData().forEach((node) => {
      const matchesSearch = node.label
        ?.toLowerCase()
        .includes(normalizedSearch);
      const children = node.children
        ? this.filterNodes(node.children, normalizedSearch)
        : [];

      if (matchesSearch || children.length > 0) {
        filtered.push({
          ...node,
          children: children.length > 0 ? children : undefined,
        });
      }
    });

    this.filteredTreeData.set(filtered);
  }

  /**
   * Recursively filter tree nodes
   */
  private filterNodes(nodes: TreeNode[], searchText: string): TreeNode[] {
    const filtered: TreeNode[] = [];

    nodes.forEach((node) => {
      const matchesSearch = node.label
        ?.toLowerCase()
        .includes(searchText.toLowerCase());
      const children = node.children
        ? this.filterNodes(node.children, searchText)
        : [];

      // Include node if it matches search OR if any of its descendants match
      if (matchesSearch || children.length > 0) {
        filtered.push({
          ...node,
          children: children.length > 0 ? children : undefined,
        });
      }
    });

    return filtered;
  }

  /**
   * Convert KeycloakGroupResponse to TreeNode format
   */
  private convertToTreeNodes(roles: KeycloakGroupResponse[]): TreeNode[] {
    return roles.map((role) => this.convertRoleToTreeNode(role));
  }

  /**
   * Convert single role to TreeNode
   */
  private convertRoleToTreeNode(role: KeycloakGroupResponse): TreeNode {
    const roleAny = role as any;

    return {
      key: role.id || '',
      label: role.name || '',
      data: {
        id: role.id || '',
        name: role.name || '',
        code: role.name || '',
        description: role.description || '',
        path: role.path || '',
        permissions: [],
        subGroups: role.subGroups || [],
        parentId: role.parentId || '',
      },
      children:
        role.subGroups && role.subGroups.length > 0
          ? role.subGroups.map((child) => this.convertRoleToTreeNode(child))
          : undefined,
      expanded: false,
    };
  }

  /**
   * Get display name for role with i18n mapping
   */
  getRolesDisplayText(role: any): string {
    if (!role.name) return '';

    // Try to get i18n translation for role name
    const i18nKey = `employee.job_position.${role.name}`;
    const translatedName = this.translateService.instant(i18nKey);

    // If translation key doesn't exist, use original name
    const displayName = translatedName !== i18nKey ? translatedName : role.name;
    const childCount = role.subGroups?.length || 0;

    if (role.parentId) {
      return `${displayName}`;
    }

    return `${displayName} (${childCount})`;
  }

  /**
   * Handle tree node selection - allow all nodes to be selected
   */
  onNodeSelect(node: TreeNode): void {
    // Allow selection for all nodes (both parent and child)
    this.selectedNode.set(node);

    // Create SelectedSidebarItem and emit to store
    const selectedItem: SelectedSidebarItem = {
      id: node.data.id,
      name: node.data.name,
      path: node.data.path,
      parentId: node.data.parentId,
    };

    // Update store with selected item
    this.roleStoreService.setSelectedSidebarItem(selectedItem);

    // Emit for backward compatibility (optional)
    this.roleSelect.emit(node.data);
  }

  /**
   * Handle "All Roles" click
   */
  onAllRolesClick(): void {
    this.selectedNode.set(null);

    // Reset store to show parent only
    this.roleStoreService.resetToParentOnly();

    // Emit for backward compatibility (optional)
    this.roleSelect.emit(null);
  }

  private resetSelectionForSearch(): void {
    this.selectedNode.set(null);
    this.roleStoreService.resetToParentOnly();
    this.roleSelect.emit(null);
    this.cdr.detectChanges();
  }

  private selectCreatedRole(roleName: string): void {
    let sub: Subscription | null = null;

    sub = this.roleStoreService.originalData$.subscribe((roles) => {
      if (!roles?.length) {
        return;
      }

      const createdRole = this.findRoleByName(roles, roleName);
      if (!createdRole) {
        return;
      }

      const selectedNode = this.convertRoleToTreeNode(createdRole);
      this.selectedNode.set(selectedNode);

      const selectedItem: SelectedSidebarItem = {
        id: selectedNode.data.id,
        name: selectedNode.data.name,
        path: selectedNode.data.path,
        parentId: selectedNode.data.parentId,
      };

      this.roleStoreService.setSelectedSidebarItem(selectedItem);
      this.roleSelect.emit(selectedNode.data);
      this.cdr.detectChanges();
      this.scrollToRole(roleName);
      sub?.unsubscribe();
    });

    this.roleStoreService.refresh();
  }

  private findRoleByName(
    roles: KeycloakGroupResponse[],
    roleName: string,
  ): KeycloakGroupResponse | null {
    for (const role of roles) {
      if (role.name === roleName) {
        return role;
      }

      if (role.subGroups?.length) {
        const found = this.findRoleByName(role.subGroups, roleName);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  private scrollToRole(roleName: string): void {
    this.tryScrollToRole(roleName, 0);
  }

  private tryScrollToRole(roleName: string, attempt: number): void {
    const treeContainer = this.hostElement.nativeElement.querySelector(
      '.tree-container',
    ) as HTMLElement | null;
    const roleNodes = Array.from(
      this.hostElement.nativeElement.querySelectorAll('.tree-node-content'),
    ) as HTMLElement[];
    const targetNode = roleNodes.find(
      (node: HTMLElement) => node.dataset['roleName'] === roleName,
    );

    if (!treeContainer || !targetNode) {
      if (attempt >= 10) {
        return;
      }

      requestAnimationFrame(() => {
        this.tryScrollToRole(roleName, attempt + 1);
      });
      return;
    }

    const containerRect = treeContainer.getBoundingClientRect();
    const targetRect = targetNode.getBoundingClientRect();
    const topOffset =
      targetRect.top - containerRect.top + treeContainer.scrollTop;
    const centeredScrollTop =
      topOffset - treeContainer.clientHeight / 2 + targetRect.height / 2;
    const nextScrollTop = Math.max(0, centeredScrollTop);

    treeContainer.scrollTo({
      top: nextScrollTop,
      behavior: 'smooth',
    });
  }
}
